import type { ReactNode } from 'react';
import { Link, Route, Routes } from 'react-router-dom';
import { SiteFooter } from './components/SiteFooter';
import { SiteHeader } from './components/SiteHeader';
import { textLink } from './lib/ui';
import { Home } from './pages/Home';
import { MatchInvite } from './pages/MatchInvite';
import { Privacy } from './pages/Privacy';
import { Rankings } from './pages/Rankings';
import { Support } from './pages/Support';
import { Terms } from './pages/Terms';
import { TournamentBracket } from './pages/TournamentBracket';

/**
 * トップ以外のページの幅。**トップはヘッダーと同じ広い幅**（`siteContainer`）で節ごとに組むが、
 * 表や長い文を読むページはこれまでどおり狭くする。
 */
function Page({ children }: { children: ReactNode }) {
  return <div className="mx-auto w-full max-w-5xl px-4">{children}</div>;
}

export function App() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />

      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route
            path="/rankings"
            element={
              <Page>
                <Rankings />
              </Page>
            }
          />
          {/* App Store Connect がプライバシーポリシーの URL を必須にしている（#121）。 */}
          <Route
            path="/privacy"
            element={
              <Page>
                <Privacy />
              </Page>
            }
          />
          {/*
            利用規約（#295）。Google の OAuth の同意画面に、ホームページ・プライバシーポリシーと並べて載せる。
          */}
          <Route
            path="/terms"
            element={
              <Page>
                <Terms />
              </Page>
            }
          />
          {/* 問い合わせ先（#260）。App Review 1.2 の連絡先の公開と、App Store Connect のサポート URL。 */}
          <Route
            path="/support"
            element={
              <Page>
                <Support />
              </Page>
            }
          />
          {/*
            トーナメント表（#151）。**ログイン不要**。通知からアプリに戻れない人
            （アプリを消した・別の端末）の受け皿になる（企画書 §17.2 / §37-4）。
          */}
          <Route
            path="/tournaments/:id"
            element={
              <Page>
                <TournamentBracket />
              </Page>
            }
          />
          {/*
            プライベートマッチの招待リンク（#153 / #217）。アプリが入っていれば Universal Links で
            アプリが開くので、ここに来るのはアプリが無い端末など。
          */}
          <Route
            path="/m/:code"
            element={
              <Page>
                <MatchInvite />
              </Page>
            }
          />
          {/*
            将来ここに追加する想定（企画書 §19）:
            /characters/:id — キャラクターカードの OGP 対応ページ（SNS 共有の着地点）
            ルーティングを塞がないよう、トップレベルの階層を空けてある。
          */}
          <Route
            path="*"
            element={
              <Page>
                <NotFound />
              </Page>
            }
          />
        </Routes>
      </main>

      <SiteFooter />
    </div>
  );
}

function NotFound() {
  return (
    <div className="pt-8">
      <h1 className="font-display text-xl">ページが見つかりません</h1>
      <p className="mt-2 text-sm text-text-muted">
        <Link to="/" className={`${textLink} text-text`}>
          ホームへ戻る
        </Link>
      </p>
    </div>
  );
}
